import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import AdminPageShell from '../../components/admin/AdminPageShell';
import Link from '../../components/Link';
import { HELP_ARTICLES, getHelpArticle } from './helpArticles';
import { HELP_ILLUSTRATIONS } from './HelpScreenFrame';
import HelpContacts from './HelpContacts';

function articlePath(id) {
  return `/help/${id}`;
}

function Help({ classes, match }) {
  const articleId = match && match.params ? match.params.articleId : undefined;
  const article = getHelpArticle(articleId);
  const Illustration = article ? HELP_ILLUSTRATIONS[article.illustration] : null;

  if (articleId && !article) {
    return (
      <AdminPageShell title="Помощь" subtitle="Инструкции" data-testid="admin-help">
        <Paper className={classes.paper} elevation={2}>
          <Typography variant="h6" className={classes.notFoundTitle}>
            Статья не найдена
          </Typography>
          <Button component={Link} to="/help" color="primary">
            К списку инструкций
          </Button>
        </Paper>
      </AdminPageShell>
    );
  }

  if (article) {
    return (
      <AdminPageShell
        title={article.title}
        subtitle="Инструкции"
        maxWidth={960}
        data-testid="admin-help-article"
        beforeHeader={
          <Button className={classes.back} component={Link} to="/help">
            ← К списку инструкций
          </Button>
        }
        actions={
          <Button component={Link} to={article.href} variant="contained" color="primary">
            {article.hrefLabel}
          </Button>
        }
      >
        <Paper className={classes.articlePaper} elevation={2}>
          {Illustration ? (
            <div className={classes.illustrationWrap}>
              <Illustration />
            </div>
          ) : null}
          <ol className={classes.steps}>
            {article.steps.map((step, index) => (
              <li key={index} className={classes.step}>
                <Typography variant="body1">{step.text}</Typography>
                {step.href ? (
                  <Button
                    className={classes.stepLink}
                    component={Link}
                    to={step.href}
                    color="primary"
                    size="small"
                  >
                    {step.hrefLabel || 'Открыть'}
                  </Button>
                ) : null}
              </li>
            ))}
          </ol>
        </Paper>
        <div className={classes.contactsAfter}>
          <HelpContacts />
        </div>
        <nav className={classes.more} aria-label="Другие инструкции">
          {HELP_ARTICLES.filter((item) => item.id !== article.id).map((item) => (
            <Button key={item.id} component={Link} to={articlePath(item.id)} className={classes.moreLink}>
              {item.title}
            </Button>
          ))}
        </nav>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="Помощь"
      subtitle="Короткие инструкции по панели: центры, площадки, расписание, секции и клиенты."
      data-testid="admin-help"
    >
      <div className={classes.contactsFirst}>
        <HelpContacts />
      </div>
      <div className={classes.list}>
        {HELP_ARTICLES.map((item) => {
          const Preview = HELP_ILLUSTRATIONS[item.illustration];
          return (
            <Paper
              key={item.id}
              className={classes.card}
              elevation={2}
            >
              {Preview ? (
                <div className={classes.cardPreview} aria-hidden="true">
                  <Preview />
                </div>
              ) : null}
              <Typography variant="h6" className={classes.cardTitle}>
                {item.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" className={classes.cardSummary}>
                {item.summary}
              </Typography>
              <Button
                className={classes.cardButton}
                component={Link}
                to={articlePath(item.id)}
                variant="contained"
                color="primary"
              >
                Подробнее
              </Button>
            </Paper>
          );
        })}
      </div>
    </AdminPageShell>
  );
}

Help.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      articleId: PropTypes.string,
    }),
  }),
};

Help.defaultProps = {
  match: { params: {} },
};

const styles = (theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(4, 3),
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    backgroundColor: theme.palette.background.paper,
  },
  notFoundTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  back: {
    textTransform: 'none',
    paddingLeft: 0,
    minWidth: 0,
    color: theme.palette.text.secondary,
  },
  articlePaper: {
    width: '100%',
    padding: theme.spacing(3),
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  illustrationWrap: {
    marginBottom: theme.spacing(3),
  },
  steps: {
    margin: 0,
    paddingLeft: theme.spacing(3),
  },
  step: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  stepLink: {
    marginTop: theme.spacing(0.5),
    textTransform: 'none',
  },
  more: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(3),
  },
  moreLink: {
    textTransform: 'none',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  contactsFirst: {
    width: '100%',
    maxWidth: 880,
    marginBottom: 32,
  },
  contactsAfter: {
    width: '100%',
    marginTop: 24,
  },
  list: {
    display: 'block',
    width: '100%',
    maxWidth: 880,
  },
  card: {
    display: 'block',
    padding: theme.spacing(3),
    marginBottom: 32,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  cardPreview: {
    marginBottom: theme.spacing(2.5),
    pointerEvents: 'none',
    overflow: 'hidden',
    borderRadius: 8,
    '& figcaption': {
      display: 'none',
    },
  },
  cardTitle: {
    fontSize: '1.0625rem',
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.primary,
  },
  cardSummary: {
    marginBottom: theme.spacing(2),
  },
  cardButton: {
    textTransform: 'none',
  },
});

export default withStyles(styles)(Help);
